'use client';

import { useCallback, useMemo, useState } from 'react';

type CanGoNextArgs = {
  currentStep: number;
  isSuccess: boolean;
  isError: boolean;
  isFinalStepSubmitting: boolean;
  selectedConsultationTitle: string;
  selectedTopics: string[];
  anliegen: string;
};

const canGoNextForStep = ({
  currentStep,
  isSuccess,
  isError,
  isFinalStepSubmitting,
  selectedConsultationTitle,
  selectedTopics,
  anliegen,
}: CanGoNextArgs): boolean => {
  if (isSuccess || isError || isFinalStepSubmitting) return false;

  if (currentStep === 1) return Boolean(selectedConsultationTitle);

  if (currentStep === 2)
    return selectedTopics.length > 0 || anliegen.trim().length > 0;

  return true;
};

const shouldShowFooter = ({
  isSuccess,
  isError,
  isFinalStepSubmitting,
}: {
  isSuccess: boolean;
  isError: boolean;
  isFinalStepSubmitting: boolean;
}): boolean => !isSuccess && !isError && !isFinalStepSubmitting;

const getStepIndicatorCurrent = ({
  isSuccess,
  currentStep,
  totalSteps,
}: {
  isSuccess: boolean;
  currentStep: number;
  totalSteps: number;
}): number => (isSuccess ? totalSteps : currentStep);

type UseMultistepNavigationArgs = {
  totalSteps: number;
  isSuccess: boolean;
  isError: boolean;
  isFinalStepSubmitting: boolean;
  selectedConsultationTitle: string;
  selectedTopics: string[];
  anliegen: string;
  onFinalSubmit: () => void;
  lockBackNavigation?: boolean;
  skipFirstStep?: boolean;
};

export const useMultistepNavigation = (args: UseMultistepNavigationArgs) => {
  const { totalSteps, onFinalSubmit } = args;
  const firstReachableStep = args.skipFirstStep ? 2 : 1;

  const [currentStep, setCurrentStep] = useState<number>(firstReachableStep);

  const canGoBack = useMemo(
    () =>
      currentStep > firstReachableStep &&
      !args.isSuccess &&
      !args.isError &&
      !args.isFinalStepSubmitting &&
      !args.lockBackNavigation,
    [
      currentStep,
      firstReachableStep,
      args.isSuccess,
      args.isError,
      args.isFinalStepSubmitting,
      args.lockBackNavigation,
    ],
  );

  const canGoNext = useMemo(
    () =>
      canGoNextForStep({
        currentStep,
        isSuccess: args.isSuccess,
        isError: args.isError,
        isFinalStepSubmitting: args.isFinalStepSubmitting,
        selectedConsultationTitle: args.selectedConsultationTitle,
        selectedTopics: args.selectedTopics,
        anliegen: args.anliegen,
      }),
    [
      currentStep,
      args.isSuccess,
      args.isError,
      args.isFinalStepSubmitting,
      args.selectedConsultationTitle,
      args.selectedTopics,
      args.anliegen,
    ],
  );

  const showFooter = useMemo(
    () =>
      shouldShowFooter({
        isSuccess: args.isSuccess,
        isError: args.isError,
        isFinalStepSubmitting: args.isFinalStepSubmitting,
      }),
    [args.isSuccess, args.isError, args.isFinalStepSubmitting],
  );

  const stepIndicatorCurrent = useMemo(
    () =>
      getStepIndicatorCurrent({
        isSuccess: args.isSuccess,
        currentStep,
        totalSteps,
      }),
    [args.isSuccess, currentStep, totalSteps],
  );

  const onNext = useCallback(() => {
    if (!canGoNext) return;

    if (currentStep === totalSteps) {
      onFinalSubmit();
      return;
    }

    setCurrentStep((s) => Math.min(totalSteps, s + 1));
  }, [canGoNext, currentStep, totalSteps, onFinalSubmit]);

  const onBack = useCallback(() => {
    if (!canGoBack) return;
    setCurrentStep((s) => Math.max(firstReachableStep, s - 1));
  }, [canGoBack, firstReachableStep]);

  const resetNavigation = useCallback(() => {
    setCurrentStep(firstReachableStep);
  }, [firstReachableStep]);

  return {
    totalSteps,
    currentStep,
    canGoBack,
    canGoNext,
    showFooter,
    stepIndicatorCurrent,
    onNext,
    onBack,
    resetNavigation,
    setCurrentStep,
  } as const;
};

export default useMultistepNavigation;
